import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Receipt, Plus, PencilSimple, TrashSimple, MagnifyingGlass,
  Buildings, UsersThree, CheckCircle, Calculator,
  X, ClockCounterClockwise, CurrencyCircleDollar, FilePdf, MagicWand,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const STATUS_META = {
  taslak: { label: "Taslak", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  onaylandi: { label: "Onaylandı", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  odendi: { label: "Ödendi", cls: "bg-blue-50 text-[#1B4776] border-blue-200" },
  iptal: { label: "İptal", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const TEVKIFAT_OPTIONS = ["yok", "2/10", "3/10", "4/10", "5/10", "7/10", "9/10"];

const fmt = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "-"
    : Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyManualDed = { code: "custom", name: "", base: 0, rate: 0, amount: 0, auto: false };

export default function SubProgress() {
  const [params, setParams] = useSearchParams();
  const initialContract = params.get("contract_id") || "all";
  const { sites } = useSites();
  // Faz 34.58: URL'de site_id → şantiye scoped görünüm (SubContracts ile aynı desen)
  const { siteId } = useParams();
  const currentSite = useMemo(
    () => (sites || []).find((s) => s.id === siteId),
    [sites, siteId],
  );
  const isSiteScoped = !!siteId;
  const [rows, setRows] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractFilter, setContractFilter] = useState(initialContract);
  const [siteFilter, setSiteFilter] = useState(siteId || "all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null); // her hakediş için özel yapı
  // Faz 34.57 — Yeni Hakediş için Taşeron + Sözleşme seçim diyaloğu
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSub, setPickerSub] = useState("");
  const [pickerContract, setPickerContract] = useState("");
  const confirm = useConfirm();

  const load = async () => {
    try {
      const p = {};
      if (contractFilter !== "all") p.contract_id = contractFilter;
      // Şantiye scoped görünümdeyken URL siteId öncelikli filtre
      if (isSiteScoped) p.site_id = siteId;
      else if (siteFilter !== "all") p.site_id = siteFilter;
      if (statusFilter !== "all") p.status = statusFilter;
      const { data } = await axios.get(`${API}/subprogress`, { params: p });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadContracts = async () => {
    try {
      const { data } = await axios.get(`${API}/subcontracts`);
      setContracts(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [contractFilter, siteFilter, statusFilter, siteId]); // eslint-disable-line
  useEffect(() => { loadContracts(); }, []);

  // Şantiye scoped görünümde siteFilter'ı URL siteId'ye kilitle
  useEffect(() => {
    if (isSiteScoped && siteFilter !== siteId) setSiteFilter(siteId);
  }, [isSiteScoped, siteId]); // eslint-disable-line

  // Şantiye değişince o şantiyeye ait olmayan sözleşme seçimini sıfırla
  useEffect(() => {
    if (siteFilter === "all" || contractFilter === "all") return;
    const c = contracts.find((x) => x.id === contractFilter);
    if (c && c.site_id !== siteFilter) {
      setContractFilter("all");
      setParams({});
    }
  }, [siteFilter, contracts, contractFilter, setParams]);

  // Sözleşme dropdown'ında sadece seçili şantiyenin sözleşmeleri
  const visibleContracts = useMemo(() => {
    if (siteFilter === "all") return contracts;
    return contracts.filter((c) => c.site_id === siteFilter);
  }, [contracts, siteFilter]);

  // Yeni Hakediş picker: sözleşmelerden benzersiz taşeron listesi
  // (aktif + taslak sözleşmeler dahil — sadece iptal/tamamlandı hariç)
  const _isPickable = (c) => !["iptal", "tamamlandi"].includes(c.status || "");
  const pickerSubs = useMemo(() => {
    const map = {};
    for (const c of visibleContracts) {
      if (!_isPickable(c)) continue;
      const sid = c.subcontractor_id;
      if (!sid || map[sid]) continue;
      map[sid] = {
        id: sid, name: c.subcontractor_name || "(isimsiz)",
        kind: c.subcontractor_kind || "alt_yuklenici",
      };
    }
    return Object.values(map).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "tr"));
  }, [visibleContracts]);

  // Picker: seçili taşerona ait (iptal/tamamlandı hariç) sözleşmeler
  const pickerContracts = useMemo(() => {
    if (!pickerSub) return [];
    return visibleContracts.filter((c) =>
      c.subcontractor_id === pickerSub && _isPickable(c));
  }, [visibleContracts, pickerSub]);

  const openNew = async (contractIdArg) => {
    const cid = contractIdArg || contractFilter;
    if (!cid || cid === "all") {
      // Sözleşme belirtilmediyse taşeron+sözleşme seçim diyaloğunu aç
      setPickerSub("");
      setPickerContract("");
      setPickerOpen(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/subprogress/preview/${cid}`);
      const c = contracts.find((x) => x.id === cid);
      setEditing(null);
      setForm({
        contract_id: cid,
        contract_code: c?.code || "",
        contract_title: c?.title || "",
        subcontractor_name: c?.subcontractor_name || "",
        site_code: c?.site_code || "",
        no: data.no,
        period_start: new Date().toISOString().slice(0, 10),
        period_end: new Date().toISOString().slice(0, 10),
        issue_date: new Date().toISOString().slice(0, 10),
        items: data.items,
        advance_deduction: 0,
        advance_remaining: data.advance_remaining,
        kdv_tevkifat: data.kdv_tevkifat,
        damga_rate: data.damga_rate,
        retention_rate: data.retention_rate,
        provisional_retention_rate: data.provisional_retention_rate,
        stopaj_rate: data.stopaj_rate,
        vat_rate: data.vat_rate,
        formality: data.formality || c?.formality || "resmi",
        manual_deductions: [],
        status: "taslak",
        notes: "",
      });
      setOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openEdit = async (r) => {
    try {
      const { data: contract } = await axios.get(`${API}/subcontracts/${r.contract_id}`);
      // Auto/manuel kesintileri ayır
      const auto = (r.deductions || []).filter((d) => d.auto);
      const manual = (r.deductions || []).filter((d) => !d.auto);
      // Auto oranları çek
      const kdvT = auto.find((d) => d.code === "kdv_tevkifat");
      const damga = auto.find((d) => d.code === "damga");
      const teminat = auto.find((d) => d.code === "teminat");
      const geciciTeminat = auto.find((d) => d.code === "gecici_teminat");
      const stopaj = auto.find((d) => d.code === "stopaj");
      setEditing(r);
      setForm({
        contract_id: r.contract_id,
        contract_code: r.contract_code,
        contract_title: r.contract_title,
        subcontractor_name: r.subcontractor_name,
        site_code: r.site_code,
        no: r.no,
        period_start: r.period_start || "",
        period_end: r.period_end || "",
        issue_date: r.issue_date || "",
        items: r.items || [],
        advance_deduction: r.advance_deduction || 0,
        advance_remaining: (contract.advance_amount || 0) - (contract.advance_deducted || 0) + (r.advance_deduction || 0),
        kdv_tevkifat: contract.kdv_tevkifat,
        damga_rate: damga?.rate ?? contract.damga_rate,
        retention_rate: teminat?.rate ?? contract.retention_rate,
        provisional_retention_rate: geciciTeminat?.rate ?? contract.provisional_retention_rate ?? 0,
        stopaj_rate: stopaj?.rate ?? contract.stopaj_rate,
        vat_rate: contract.vat_rate,
        formality: contract.formality || "resmi",
        manual_deductions: manual.map((m) => ({ ...emptyManualDed, ...m, auto: false })),
        status: r.status,
        notes: r.notes || "",
      });
      setOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Item cumulative_qty değişince current_qty ve amount hesapla
  const updateItemCumulative = (idx, cumulativeQty) => {
    setForm((f) => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        const cum = Number(cumulativeQty) || 0;
        const prev = Number(it.previous_cum_qty) || 0;
        const currentQty = cum - prev;
        return {
          ...it,
          cumulative_qty: cum,
          current_qty: currentQty,
          current_amount: currentQty * (Number(it.unit_price) || 0),
        };
      });
      return { ...f, items };
    });
  };

  // İlave (ekstra) kalem — sözleşmede olmayan bir kalemi hakedişe ekle
  const addExtraItem = () => {
    setForm((f) => ({
      ...f,
      items: [
        ...(f.items || []),
        {
          contract_item_no: "",
          name: "",
          unit: "",
          unit_price: 0,
          vat_rate: Number(f.default_vat_rate ?? f.vat_rate ?? 20),
          contract_qty: 0,
          previous_cum_qty: 0,
          cumulative_qty: 0,
          current_qty: 0,
          current_amount: 0,
          wbs_node_id: "",
          is_extra: true,
        },
      ],
    }));
  };
  const updateExtraField = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => {
        if (i !== idx || !it.is_extra) return it;
        const patch = { [field]: value };
        if (field === "unit_price") {
          const up = Number(value) || 0;
          const cq = Number(it.current_qty) || 0;
          patch.unit_price = up;
          patch.current_amount = up * cq;
        }
        return { ...it, ...patch };
      }),
    }));
  };
  const removeExtraItem = (idx) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((it, i) => !(i === idx && it.is_extra)),
    }));
  };
  const [pushExtrasToContract, setPushExtrasToContract] = useState(false);
  const hasExtras = (form?.items || []).some((it) => it.is_extra);

  // WBS'ten Otomatik Doldur (Faz 28 — Hakediş Otomasyonu)
  const [autoFilling, setAutoFilling] = useState(false);
  const autoFillFromWbs = async () => {
    if (!form.contract_id) return;
    if (!form.period_end) {
      toast.error("Önce hakediş bitiş tarihini (period_end) girin");
      return;
    }
    const periodMonth = String(form.period_end).slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(periodMonth)) {
      toast.error("Geçerli bir dönem seçin");
      return;
    }
    setAutoFilling(true);
    try {
      const params = { period_end_month: periodMonth };
      if (editing?.id) params.exclude_progress_id = editing.id;
      const { data } = await axios.get(
        `${API}/subprogress/auto-suggest/${form.contract_id}`,
        { params },
      );
      // Sözleşme kalemi no üzerinden eşleştir
      const suggMap = {};
      for (const s of data.items || []) {
        const key = s.contract_item_no || s.name || "";
        suggMap[key] = s;
      }
      let applied = 0;
      setForm((f) => {
        const items = f.items.map((it) => {
          const key = it.contract_item_no || it.name || "";
          const s = suggMap[key];
          if (!s || !s.suggested) return it;
          const cum = Number(s.cumulative_qty_suggested) || 0;
          const prev = Number(it.previous_cum_qty) || 0;
          const currentQty = cum - prev;
          applied += 1;
          return {
            ...it,
            cumulative_qty: cum,
            current_qty: currentQty,
            current_amount: currentQty * (Number(it.unit_price) || 0),
          };
        });
        return { ...f, items };
      });
      const parts = [`${applied} kalem WBS'ten dolduruldu`];
      if (data.unmapped_count) parts.push(`${data.unmapped_count} kalem WBS'e bağlı değil`);
      if (data.no_metraj_count) parts.push(`${data.no_metraj_count} kalem metrajsız`);
      toast.success(parts.join(" · "));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setAutoFilling(false);
    }
  };

  // Canlı hesaplamalar (frontend preview) — backend son sözü söyler
  const calc = useMemo(() => {
    if (!form) return null;
    const isInformal = (form.formality || "resmi") === "gayri_resmi";
    const gross = form.items.reduce((s, it) => {
      const cum = Number(it.cumulative_qty) || 0;
      const prev = Number(it.previous_cum_qty) || 0;
      return s + (cum - prev) * (Number(it.unit_price) || 0);
    }, 0);
    // Kümülatif ve önceki kümülatif gross — Teminat için (Faz 32.2)
    const cumulativeGross = form.items.reduce((s, it) => {
      const cum = Number(it.cumulative_qty) || 0;
      return s + cum * (Number(it.unit_price) || 0);
    }, 0);
    const previousGross = cumulativeGross - gross;
    // Gayri resmi sözleşmelerde KDV/tevkifat/damga/stopaj uygulanmaz.
    const vatRate = isInformal
      ? 0
      : (form.vat_rate === null || form.vat_rate === undefined ? 20 : Number(form.vat_rate));
    const vat = isInformal ? 0 : gross * vatRate / 100;
    const grossWithVat = gross + vat;
    // Otomatik kesintiler
    const auto = [];
    // Tevkifat
    let tevRate = 0;
    if (!isInformal && form.kdv_tevkifat && form.kdv_tevkifat !== "yok") {
      const [a, b] = form.kdv_tevkifat.split("/");
      tevRate = Number(a) / Number(b);
    }
    if (tevRate > 0) {
      auto.push({
        code: "kdv_tevkifat", name: `KDV Tevkifatı (${form.kdv_tevkifat})`,
        base: vat, rate: tevRate * 100, amount: vat * tevRate, auto: true,
      });
    }
    if (!isInformal && form.damga_rate > 0) {
      auto.push({
        code: "damga", name: "Damga Vergisi",
        base: gross, rate: form.damga_rate, amount: gross * form.damga_rate / 100, auto: true,
      });
    }
    // Kesin Teminat — Kümülatif A üzerinden, önceki kesim iade edilir
    if (form.retention_rate > 0) {
      auto.push({
        code: "teminat", name: "Kesin Teminat Kesintisi (Kümülatif)",
        base: cumulativeGross, rate: form.retention_rate,
        amount: cumulativeGross * form.retention_rate / 100, auto: true,
      });
      if (previousGross > 0.005) {
        auto.push({
          code: "teminat_iade",
          name: "Önceki Hakedişte Kesilen Kesin Teminat (İade)",
          base: previousGross, rate: form.retention_rate,
          amount: -previousGross * form.retention_rate / 100, auto: true,
          is_credit: true,
        });
      }
    }
    // Geçici Teminat — Kümülatif A üzerinden, önceki kesim iade edilir
    if (form.provisional_retention_rate > 0) {
      auto.push({
        code: "gecici_teminat", name: "Geçici Teminat Kesintisi (Kümülatif)",
        base: cumulativeGross, rate: form.provisional_retention_rate,
        amount: cumulativeGross * form.provisional_retention_rate / 100, auto: true,
      });
      if (previousGross > 0.005) {
        auto.push({
          code: "gecici_teminat_iade",
          name: "Önceki Hakedişte Kesilen Geçici Teminat (İade)",
          base: previousGross, rate: form.provisional_retention_rate,
          amount: -previousGross * form.provisional_retention_rate / 100, auto: true,
          is_credit: true,
        });
      }
    }
    if (!isInformal && form.stopaj_rate > 0) {
      auto.push({
        code: "stopaj", name: "Stopaj (Gelir V.)",
        base: gross, rate: form.stopaj_rate, amount: gross * form.stopaj_rate / 100, auto: true,
      });
    }
    if (form.advance_deduction > 0) {
      auto.push({
        code: "avans", name: "Avans Mahsubu",
        base: form.advance_deduction, rate: 0, amount: form.advance_deduction, auto: true,
      });
    }
    const manual = (form.manual_deductions || []).map((m) => {
      const base = Number(m.base) || 0;
      const rate = Number(m.rate) || 0;
      let amt = Number(m.amount) || 0;
      if (amt === 0 && base > 0 && rate > 0) amt = base * rate / 100;
      return { ...m, base, rate, amount: amt };
    });
    const totalDed = [...auto, ...manual].reduce((s, d) => s + d.amount, 0);
    return {
      gross, vat, grossWithVat,
      autoDed: auto, manualDed: manual,
      totalDed, netPayable: grossWithVat - totalDed,
    };
  }, [form]);

  const save = async () => {
    if (!form) return;
    try {
      // Advance validation
      if (form.advance_deduction > form.advance_remaining + 0.01) {
        toast.error(`Avans mahsubu kalan avansı (${fmt(form.advance_remaining)}) aşamaz`);
        return;
      }
      // İlave kalem validasyonu — Poz No ve Tanım zorunlu
      const extras = (form.items || []).filter((it) => it.is_extra);
      const missing = extras.find((it) =>
        !String(it.contract_item_no || "").trim() ||
        !String(it.name || "").trim() ||
        !(Number(it.unit_price) > 0),
      );
      if (missing) {
        toast.error("İlave kalemler için Poz No, Tanım ve Birim Fiyat zorunludur");
        return;
      }
      // İlave Poz No sözleşme kalemleriyle çakışmasın
      const contractNos = new Set(
        (form.items || []).filter((it) => !it.is_extra)
          .map((it) => String(it.contract_item_no || "").trim())
          .filter(Boolean),
      );
      const conflict = extras.find((it) =>
        contractNos.has(String(it.contract_item_no || "").trim()),
      );
      if (conflict) {
        toast.error(`Poz No "${conflict.contract_item_no}" sözleşmede zaten var — farklı bir numara girin`);
        return;
      }
      const payload = {
        contract_id: form.contract_id,
        period_start: form.period_start,
        period_end: form.period_end,
        issue_date: form.issue_date,
        items: form.items.map((it) => ({
          contract_item_no: it.contract_item_no,
          name: it.name, unit: it.unit,
          unit_price: it.unit_price, vat_rate: it.vat_rate,
          contract_qty: it.contract_qty,
          cumulative_qty: it.cumulative_qty,
          previous_cum_qty: it.previous_cum_qty,
          current_qty: it.current_qty,
          current_amount: it.current_amount,
          wbs_node_id: it.wbs_node_id || "",
          is_extra: !!it.is_extra,
        })),
        deductions: [
          // Auto kesintilerin override oranlarını bildir
          { code: "kdv_tevkifat", auto: true, rate: null }, // backend contract'tan çekecek
          { code: "damga", auto: true, rate: form.damga_rate },
          { code: "teminat", auto: true, rate: form.retention_rate },
          { code: "gecici_teminat", auto: true, rate: form.provisional_retention_rate ?? 0 },
          { code: "stopaj", auto: true, rate: form.stopaj_rate },
          ...(form.manual_deductions || []).map((m) => ({ ...m, auto: false })),
        ],
        advance_deduction: form.advance_deduction || 0,
        status: form.status,
        notes: form.notes,
        push_extras_to_contract: pushExtrasToContract && extras.length > 0,
      };
      if (editing) {
        await axios.put(`${API}/subprogress/${editing.id}`, payload);
        toast.success(
          payload.push_extras_to_contract
            ? `Hakediş güncellendi · ${extras.length} ilave sözleşmeye eklendi`
            : "Hakediş güncellendi",
        );
      } else {
        await axios.post(`${API}/subprogress`, payload);
        toast.success(
          payload.push_extras_to_contract
            ? `Hakediş oluşturuldu · ${extras.length} ilave sözleşmeye eklendi`
            : "Hakediş oluşturuldu",
        );
      }
      setPushExtrasToContract(false);
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const changeStatus = async (r, status) => {
    try {
      await axios.post(`${API}/subprogress/${r.id}/status?status=${status}`);
      toast.success(`Durum: ${STATUS_META[status]?.label}`); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Hakediş Sil",
      description: `Hakediş #${r.no} silinsin mi?`,
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    try {
      const { data } = await axios.delete(`${API}/subprogress/${r.id}`);
      if (data.payments_deleted > 0) {
        toast.success(`Silindi (${data.payments_deleted} ödeme kaydı da temizlendi)`);
      } else {
        toast.success("Silindi");
      }
      load();
    } catch (e) {
      // 409 = ödeme kayıtları var, cascade onayı iste
      if (e.response?.status === 409) {
        const cascadeOk = await confirm({
          title: "Ödeme Kayıtlarıyla Birlikte Sil?",
          description: e.response.data?.detail ||
            "Bu hakedişe bağlı ödeme kayıtları var. Devam edilirse tüm ödeme kayıtları da silinecek. Bu işlem geri alınamaz.",
          confirmText: "Evet, tümünü sil", variant: "destructive",
        });
        if (!cascadeOk) return;
        try {
          const { data } = await axios.delete(
            `${API}/subprogress/${r.id}?cascade_payments=true`);
          toast.success(`Silindi (${data.payments_deleted} ödeme kaydı da temizlendi)`);
          load();
        } catch (e2) { toast.error(formatErr(e2)); }
      } else {
        toast.error(formatErr(e));
      }
    }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await axios.get(`${API}/subprogress/${r.id}/pdf`,
        { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hakedis_${r.contract_code || "SUB"}_no${r.no}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    onaylanan: rows.filter((r) => r.status === "onaylandi" || r.status === "odendi").length,
    brut: rows.reduce((s, r) => s + (r.gross_amount || 0), 0),
    net: rows.reduce((s, r) => s + (r.net_payable || 0), 0),
    kesinti: rows.reduce((s, r) => s + (Number(r.total_deductions) || 0) + (Number(r.other_deductions_total) || 0), 0),
    // Kümülatif ödeme özeti — her sözleşme için en yüksek hakediş no'daki değerleri al
    ...(() => {
      // Sözleşme bazında en son hakedişin payments_till_date ve remaining_balance'ı
      const byContract = new Map();
      for (const r of rows) {
        const cid = r.contract_id;
        if (!cid) continue;
        const cur = byContract.get(cid);
        if (!cur || (Number(r.no) || 0) > (Number(cur.no) || 0)) {
          byContract.set(cid, r);
        }
      }
      let paid = 0, remaining = 0;
      for (const r of byContract.values()) {
        paid += Number(r.payments_till_date || 0);
        remaining += Number(r.remaining_balance || 0);
      }
      return { paid, remaining };
    })(),
  }), [rows]);

  const changeContractFilter = (v) => {
    setContractFilter(v);
    if (v === "all") setParams({}); else setParams({ contract_id: v });
  };

  const addManualDed = () => setForm((f) => ({
    ...f, manual_deductions: [...(f.manual_deductions || []), { ...emptyManualDed }],
  }));
  const setManualDed = (idx, k, v) => setForm((f) => ({
    ...f,
    manual_deductions: (f.manual_deductions || []).map((d, i) => (i === idx ? { ...d, [k]: v } : d)),
  }));
  const rmManualDed = (idx) => setForm((f) => ({
    ...f, manual_deductions: (f.manual_deductions || []).filter((_, i) => i !== idx),
  }));

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "no", defaultDir: "desc",
    getters: {
      no: (r) => Number(r.no) || 0,
      contract_title: (r) => r.contract_title || r.contract_code || "",
      subcontractor_name: (r) => r.subcontractor_name || "",
      period_end: (r) => r.period_end || r.period_start || "",
      gross_amount: (r) => Number(r.gross_amount) || 0,
      total_deductions: (r) => (Number(r.total_deductions) || 0) + (Number(r.other_deductions_total) || 0),
      net_payable: (r) => Number(r.net_payable) || 0,
      payments_till_date: (r) => Number(r.payments_till_date) || 0,
      remaining_balance: (r) => Number(r.remaining_balance) || 0,
      status: (r) => r.status || "",
    },
  });

  return (
    <Shell title={currentSite ? `${currentSite.name} — Hakedişler` : "Hakedişler"}
           subtitle="Alt Yüklenici Yönetimi">
      {/* Şantiye scoped header (Faz 34.58) */}
      {currentSite && (
        <Card className="p-3 mb-3 rounded-none sm:rounded-sm border-slate-200 bg-[#F8FAFC]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Buildings size={16} weight="duotone" className="text-[#1B4776]" />
              <span className="font-mono text-[10px] font-bold text-[#1B4776]">{currentSite.code}</span>
              <span className="text-slate-700">{currentSite.name}</span>
            </div>
            <Link to="/taseron/hakedis"
                  className="text-xs text-[#1B4776] hover:underline"
                  data-testid="sp-back-to-index">
              ← Şantiye seçimine dön
            </Link>
          </div>
        </Card>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam Hakediş</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-emerald-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Onaylı / Ödendi</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.onaylanan}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-blue-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1B4776] font-bold">Brüt İş (KDV Hariç)</div>
          <div className="text-lg font-bold text-[#1B4776] mt-1 font-mono">₺{fmt(stats.brut)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-amber-50/60">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">Toplam Kesinti</div>
          <div className="text-lg font-bold text-amber-700 mt-1 font-mono">₺{fmt(stats.kesinti)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-blue-50/40"
              data-testid="sp-stat-paid">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1B4776] font-bold">Yapılan Ödeme</div>
          <div className="text-lg font-bold text-[#1B4776] mt-1 font-mono">₺{fmt(stats.paid)}</div>
        </Card>
        <Card className={`p-4 rounded-none sm:rounded-sm border-slate-200 ${
          Math.abs(stats.remaining) > 0.01 ? "bg-rose-50/50" : "bg-emerald-50/50"
        }`} data-testid="sp-stat-remaining">
          <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
            Math.abs(stats.remaining) > 0.01 ? "text-rose-700" : "text-emerald-700"
          }`}>Kalan Bakiye</div>
          <div className={`text-lg font-bold mt-1 font-mono ${
            Math.abs(stats.remaining) > 0.01 ? "text-rose-700" : "text-emerald-700"
          }`}>₺{fmt(stats.remaining)}</div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3 flex-1 items-center">
            {!isSiteScoped && (
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-52 rounded-none sm:rounded-sm"
                             data-testid="sp-site-filter">
                <Buildings size={12} weight="duotone" className="text-[#1B4776] mr-1" />
                <SelectValue placeholder="Şantiye seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şantiyeler</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono text-xs text-[#1B4776] mr-2">{s.code}</span>{s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            <Select value={contractFilter} onValueChange={changeContractFilter}>
              <SelectTrigger className="w-80 rounded-none sm:rounded-sm"
                             data-testid="sp-contract-filter"
                             disabled={siteFilter === "all"}>
                <UsersThree size={12} weight="duotone" className="text-[#556B10] mr-1" />
                <SelectValue placeholder={
                  siteFilter === "all"
                    ? "Önce şantiye seçin"
                    : "Ekip / Sözleşme seçin"
                } />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Sözleşmeler</SelectItem>
                {visibleContracts.length === 0 && siteFilter !== "all" && (
                  <div className="p-2 text-[11px] text-rose-600 italic">
                    Bu şantiyede sözleşme yok
                  </div>
                )}
                {visibleContracts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-[#1B4776]">{c.code}</span>
                      <span className="text-xs font-bold">{c.subcontractor_name}</span>
                      <span className="text-[10px] text-slate-400">— {c.title}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 rounded-none sm:rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => openNew()}
                  className="bg-[#95C11F] hover:bg-[#7ea01a] text-white rounded-none sm:rounded-sm"
                  data-testid="sp-add-btn">
            <Plus size={14} weight="bold" className="mr-1.5" /> Yeni Hakediş
          </Button>
        </div>
        {siteFilter === "all" && !isSiteScoped && (
          <div className="text-[10px] text-amber-700 mt-2 font-bold">
            📍 Yeni hakediş için önce <b>Şantiye</b> seçin; ardından <b>Taşeron / Sözleşme</b> penceresi açılacaktır.
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh label="No" k="no" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-16" />
                <SortableTh label="Sözleşme / İş" k="contract_title" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left" />
                <SortableTh label="Taşeron" k="subcontractor_name" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-44" />
                <SortableTh label="Dönem" k="period_end" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-32" />
                <SortableTh label="Brüt (KDV Hariç)" k="gross_amount" sort={sort} onSort={toggleSort} className="px-4 py-3 text-right w-32" align="right" />
                <SortableTh label="Kesinti" k="total_deductions" sort={sort} onSort={toggleSort} className="px-4 py-3 text-right w-32" align="right" />
                <SortableTh label="Net Ödenecek" k="net_payable" sort={sort} onSort={toggleSort} className="px-4 py-3 text-right w-32" align="right" />
                <SortableTh k="payments_till_date" sort={sort} onSort={toggleSort} className="px-4 py-3 text-right w-36" align="right">
                  Yapılan Ödeme<span className="block text-[8px] text-slate-400 normal-case tracking-normal">(Rapor tarihine kadar)</span>
                </SortableTh>
                <SortableTh label="Kalan Bakiye" k="remaining_balance" sort={sort} onSort={toggleSort} className="px-4 py-3 text-right w-32" align="right" />
                <SortableTh label="Durum" k="status" sort={sort} onSort={toggleSort} className="px-4 py-3 text-center w-24" align="center" />
                <th className="px-4 py-3 text-right w-28">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid="sp-table-body">
              {sortedRows.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-16 text-center text-slate-400">
                  <Receipt size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Henüz hakediş yok — sözleşme seçip "Yeni Hakediş" oluşturun.</p>
                </td></tr>
              )}
              {sortedRows.map((r, i) => {
                const isT = r.subcontractor_kind === "taseron";
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sp-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center justify-center w-10 h-10 bg-[#F7FBE9] border-2 border-[#95C11F] font-bold text-[#556B10]">
                        {r.no}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-[10px] font-bold text-[#1B4776]">{r.contract_code}</div>
                      <div className="text-xs font-semibold text-slate-800 mt-0.5">{r.contract_title}</div>
                      {r.site_code && (
                        <span className="inline-block text-[9px] uppercase tracking-widest font-bold px-1 py-0.5 bg-blue-50 text-[#1B4776] border border-blue-200 mt-1">
                          {r.site_code}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {isT ? <UsersThree size={12} weight="bold" className="text-[#556B10]" />
                             : <Buildings size={12} weight="bold" className="text-[#1B4776]" />}
                        <span className="text-xs font-semibold">{r.subcontractor_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {r.period_start && <div>{r.period_start}</div>}
                      {r.period_end && <div className="text-slate-400">→ {r.period_end}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">
                      <div className="font-bold text-slate-800">₺{fmt(r.gross_amount)}</div>
                      {(Number(r.vat_amount) || 0) > 0.005 && (
                        <div className="text-[10px] text-slate-400">+KDV ₺{fmt(r.vat_amount)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-amber-700"
                        title={(Number(r.other_deductions_total) || 0) > 0
                          ? `Hakediş kesintileri: ₺${fmt(r.total_deductions)} · Diğer kesintiler: ₺${fmt(r.other_deductions_total)}`
                          : undefined}>
                      <div className="font-bold">
                        −₺{fmt((Number(r.total_deductions) || 0) + (Number(r.other_deductions_total) || 0))}
                      </div>
                      {(Number(r.other_deductions_total) || 0) > 0.005 && (
                        <div className="text-[9px] text-slate-500 mt-0.5 font-normal">
                          <span>₺{fmt(r.total_deductions)}</span>
                          <span className="text-slate-400 mx-0.5">+</span>
                          <span className="text-rose-700 font-semibold" title="Diğer kesintiler modülünden">
                            ₺{fmt(r.other_deductions_total)}
                            {(r.other_deductions_count || 0) > 0 && (
                              <span className="text-slate-400 ml-0.5">({r.other_deductions_count})</span>
                            )}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      <div className="text-sm font-bold text-[#556B10]">₺{fmt(r.net_payable)}</div>
                      {(r.previous_net_total || 0) > 0 && (
                        <div className="text-[9px] text-slate-400 mt-0.5">
                          Küm. ₺{fmt(r.cumulative_net)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono" data-testid={`sp-paid-cell-${i}`}>
                      <div className="text-sm font-bold text-[#1B4776]">₺{fmt(r.payments_till_date)}</div>
                      {(r.salary_payments_total || 0) > 0.005 && (
                        <div className="text-[9px] text-slate-500 mt-0.5" title="Alt yüklenici ödemesi + Ekibe yapılan maaş ödemesi">
                          <span className="text-[#1B4776] font-bold">₺{fmt(r.sub_payments_total)}</span>
                          <span className="text-slate-400 mx-1">·</span>
                          <span className="text-emerald-700 font-bold">+ ₺{fmt(r.salary_payments_total)}</span>
                          <span className="text-slate-400"> maaş</span>
                        </div>
                      )}
                      {r.payments_cutoff_date && (
                        <div className="text-[9px] text-slate-400 mt-0.5">
                          ≤ {r.payments_cutoff_date}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono" data-testid={`sp-balance-cell-${i}`}>
                      <div className={`text-sm font-bold ${
                        (r.remaining_balance || 0) > 0.01
                          ? "text-rose-600"
                          : Math.abs(r.remaining_balance || 0) <= 0.01
                            ? "text-emerald-600"
                            : "text-slate-500"
                      }`}>
                        ₺{fmt(r.remaining_balance)}
                      </div>
                      {Math.abs(r.remaining_balance || 0) <= 0.01 && (
                        <div className="text-[9px] text-emerald-600 mt-0.5 font-bold uppercase tracking-widest">
                          Tam Ödendi
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_META[r.status]?.cls || STATUS_META.taslak.cls}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.status === "taslak" && (
                          <button onClick={() => changeStatus(r, "onaylandi")}
                                  className="p-1 text-slate-400 hover:text-emerald-600"
                                  title="Onayla" data-testid={`sp-approve-${i}`}>
                            <CheckCircle size={14} weight="bold" />
                          </button>
                        )}
                        {r.status === "onaylandi" && (
                          <button onClick={() => changeStatus(r, "odendi")}
                                  className="p-1 text-slate-400 hover:text-[#1B4776]"
                                  title="Ödendi olarak işaretle" data-testid={`sp-paid-${i}`}>
                            <CurrencyCircleDollar size={14} weight="bold" />
                          </button>
                        )}
                        <button onClick={() => downloadPdf(r)} className="p-1 text-slate-400 hover:text-rose-600"
                                title="PDF İndir" data-testid={`sp-pdf-${i}`}>
                          <FilePdf size={14} weight="bold" />
                        </button>
                        <button onClick={() => openEdit(r)} className="p-1 text-slate-400 hover:text-[#1B4776]"
                                title="Düzenle" data-testid={`sp-edit-${i}`}
                                disabled={r.status === "odendi"}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => remove(r)} className="p-1 text-slate-400 hover:text-rose-600"
                                title={r.status === "odendi" ? "Ödenen hakedişi sil (ödeme kayıtları da silinir)" : "Sil"}
                                data-testid={`sp-del-${i}`}>
                          <TrashSimple size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Faz 34.57 — Yeni Hakediş: Taşeron + Sözleşme Seçim Diyaloğu */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg rounded-none sm:rounded-sm"
                       data-testid="sp-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersThree size={18} weight="duotone" className="text-[#95C11F]" />
              Yeni Hakediş — Taşeron Seçimi
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-slate-500">
              {currentSite ? (
                <>
                  <span className="font-mono font-bold text-[#1B4776]">{currentSite.code}</span>
                  {" — "}{currentSite.name}
                </>
              ) : "Tüm şantiyeler"}
            </div>
            {/* Taşeron */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 block">
                1. Taşeron / Ekip *
              </label>
              {pickerSubs.length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
                  Bu şantiyeye ait aktif sözleşme bulunamadı. Önce Sözleşmeler modülünden bir sözleşme oluşturun.
                </div>
              ) : (
                <Select value={pickerSub}
                        onValueChange={(v) => { setPickerSub(v); setPickerContract(""); }}>
                  <SelectTrigger className="rounded-none sm:rounded-sm"
                                  data-testid="sp-picker-sub">
                    <SelectValue placeholder="Taşeron / Ekip seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickerSubs.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="inline-flex items-center gap-1.5">
                          {s.kind === "ekip" ? (
                            <UsersThree size={12} weight="bold" className="text-[#556B10]" />
                          ) : (
                            <Buildings size={12} weight="bold" className="text-[#1B4776]" />
                          )}
                          {s.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {/* Sözleşme */}
            {pickerSub && (
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 block">
                  2. Sözleşme *
                </label>
                {pickerContracts.length === 0 ? (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
                    Bu taşeron için aktif sözleşme bulunamadı.
                  </div>
                ) : (
                  <Select value={pickerContract} onValueChange={setPickerContract}>
                    <SelectTrigger className="rounded-none sm:rounded-sm"
                                    data-testid="sp-picker-contract">
                      <SelectValue placeholder="Sözleşme seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {pickerContracts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="font-mono text-xs mr-2">{c.code}</span>
                          {c.title}
                          {c.formality === "gayri_resmi" && (
                            <span className="ml-2 text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 border border-amber-300 rounded-sm font-bold">GY</span>
                          )}
                          {c.status === "taslak" && (
                            <span className="ml-2 text-[9px] bg-slate-100 text-slate-700 px-1 py-0.5 border border-slate-300 rounded-sm font-bold">TASLAK</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}
                    className="rounded-none sm:rounded-sm"
                    data-testid="sp-picker-cancel">
              İptal
            </Button>
            <Button onClick={() => {
                      if (!pickerContract) {
                        toast.error("Lütfen taşeron ve sözleşme seçin");
                        return;
                      }
                      setContractFilter(pickerContract);
                      setPickerOpen(false);
                      openNew(pickerContract);
                    }}
                    disabled={!pickerContract}
                    className="bg-[#95C11F] hover:bg-[#7ea01a] text-white rounded-none sm:rounded-sm"
                    data-testid="sp-picker-continue">
              Devam Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl rounded-none sm:rounded-sm max-h-[92vh] overflow-y-auto">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt size={18} weight="duotone" className="text-[#95C11F]" />
                  {editing ? `Hakediş Düzenle #${form.no}` : `Yeni Hakediş #${form.no}`}
                  <span className="text-xs font-mono text-slate-400 ml-2">{form.contract_code}</span>
                </DialogTitle>
                <div className="text-xs text-slate-600 mt-1">
                  <span className="font-semibold">{form.contract_title}</span> —
                  <span className="ml-1">{form.subcontractor_name}</span>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {/* Dönem */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Dönem Başı</label>
                    <Input type="date" value={form.period_start}
                           onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))}
                           className="rounded-none sm:rounded-sm" data-testid="sp-period-start" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Dönem Sonu</label>
                    <Input type="date" value={form.period_end}
                           onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))}
                           className="rounded-none sm:rounded-sm" data-testid="sp-period-end" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Düzenleme Tarihi</label>
                    <Input type="date" value={form.issue_date}
                           onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))}
                           className="rounded-none sm:rounded-sm" />
                  </div>
                </div>

                {/* İş kalemleri — kümülatif tablo */}
                <div>
                  <div className="mb-2 pb-1 border-b border-[#1B4776]/20 flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] flex items-center gap-2">
                      <ClockCounterClockwise size={12} weight="bold" />
                      Kümülatif İş Kalemleri — Bu Aya Kadar Yapılan Toplam Miktarı Girin
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" variant="outline"
                              onClick={addExtraItem}
                              disabled={!form.contract_id}
                              data-testid="sp-add-extra"
                              title="Sözleşmede olmayan bir imalatı hakedişe ekle (İLAVE)"
                              className="h-7 rounded-none sm:rounded-sm border-amber-500 text-amber-700 hover:bg-amber-50">
                        <Plus size={12} weight="bold" className="mr-1" />
                        <span className="text-[10px] uppercase tracking-widest font-bold">
                          İlave Kalem Ekle
                        </span>
                      </Button>
                      <Button type="button" size="sm" variant="outline"
                              onClick={autoFillFromWbs}
                              disabled={autoFilling || !form.contract_id}
                              data-testid="sp-autofill-wbs"
                              title="Seçilen dönem sonuna kadar aylık Work Item ilerlemesine göre kümülatif metraj önerir (year_month ≤ dönem sonu ayı)"
                              className="h-7 rounded-none sm:rounded-sm border-[#95C11F] text-[#556B10] hover:bg-[#F7FBE9]">
                        <MagicWand size={12} weight="bold" className="mr-1" />
                        <span className="text-[10px] uppercase tracking-widest font-bold">
                          {autoFilling ? "Doldurluyor…" : "Aylık İlerlemeden Doldur"}
                        </span>
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[9px] uppercase tracking-widest text-slate-500 bg-slate-50">
                          <th className="px-2 py-1.5 text-left w-24">Poz</th>
                          <th className="px-2 py-1.5 text-left">Tanım</th>
                          <th className="px-2 py-1.5 text-left w-16">Birim</th>
                          <th className="px-2 py-1.5 text-right w-24">Birim Fiy.</th>
                          <th className="px-2 py-1.5 text-right w-20">Söz.Miktarı</th>
                          <th className="px-2 py-1.5 text-right w-20">Önceki Küm.</th>
                          <th className="px-2 py-1.5 text-right w-24 bg-[#F7FBE9]">Bu Aya Kadar Toplam ↴</th>
                          <th className="px-2 py-1.5 text-right w-20">Bu Ay</th>
                          <th className="px-2 py-1.5 text-right w-24">Bu Ay Tutar</th>
                          <th className="px-2 py-1.5 w-6"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((it, i) => (
                          <tr key={i}
                              className={`border-b border-slate-100 ${it.is_extra ? "bg-amber-50/40" : ""}`}
                              data-testid={`sp-item-${i}`}>
                            <td className="px-2 py-1">
                              {it.is_extra ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="inline-flex items-center gap-1 self-start px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-widest rounded-none"
                                        data-testid={`sp-item-badge-${i}`}>
                                    İlave
                                  </span>
                                  <Input value={it.contract_item_no || ""}
                                         onChange={(e) => updateExtraField(i, "contract_item_no", e.target.value)}
                                         placeholder="Poz No *"
                                         className="h-7 rounded-none sm:rounded-sm text-xs font-mono"
                                         data-testid={`sp-extra-poz-${i}`} />
                                </div>
                              ) : (
                                <span className="font-mono font-bold text-[#1B4776]">
                                  {it.contract_item_no}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1">
                              {it.is_extra ? (
                                <Input value={it.name || ""}
                                       onChange={(e) => updateExtraField(i, "name", e.target.value)}
                                       placeholder="İmalat tanımı *"
                                       className="h-7 rounded-none sm:rounded-sm text-xs"
                                       data-testid={`sp-extra-name-${i}`} />
                              ) : (
                                <>
                                  <div className="font-semibold text-slate-700">{it.name}</div>
                                  <div className="text-[10px] text-slate-400">{it.unit}</div>
                                </>
                              )}
                            </td>
                            <td className="px-2 py-1">
                              {it.is_extra ? (
                                <Input value={it.unit || ""}
                                       onChange={(e) => updateExtraField(i, "unit", e.target.value)}
                                       placeholder="m2, adet…"
                                       className="h-7 rounded-none sm:rounded-sm text-xs"
                                       data-testid={`sp-extra-unit-${i}`} />
                              ) : (
                                <span className="text-slate-500">{it.unit}</span>
                              )}
                            </td>
                            <td className="px-2 py-1">
                              {it.is_extra ? (
                                <Input type="number" step="0.01"
                                       value={it.unit_price || 0}
                                       onChange={(e) => updateExtraField(i, "unit_price", parseFloat(e.target.value) || 0)}
                                       className="h-7 rounded-none sm:rounded-sm text-xs font-mono text-right"
                                       data-testid={`sp-extra-price-${i}`} />
                              ) : (
                                <span className="font-mono text-slate-600 text-right block">
                                  ₺{fmt(it.unit_price)}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-right font-mono text-slate-500">
                              {it.is_extra ? "—" : fmt(it.contract_qty)}
                            </td>
                            <td className="px-2 py-1 text-right font-mono text-slate-500">
                              {fmt(it.previous_cum_qty)}
                            </td>
                            <td className="px-2 py-1 bg-[#F7FBE9]/60">
                              <Input type="number" step="0.01" value={it.cumulative_qty}
                                     onChange={(e) => updateItemCumulative(i, e.target.value)}
                                     className="h-8 rounded-none sm:rounded-sm text-xs font-mono text-right"
                                     data-testid={`sp-cum-${i}`} />
                            </td>
                            <td className="px-2 py-1 text-right font-mono font-bold text-slate-800">
                              {fmt(it.current_qty)}
                            </td>
                            <td className={`px-2 py-1 text-right font-mono font-bold ${it.is_extra ? "text-amber-700" : "text-[#556B10]"}`}>
                              ₺{fmt(it.current_amount)}
                            </td>
                            <td className="px-2 py-1 text-center">
                              {it.is_extra && (
                                <Button type="button" size="icon" variant="ghost"
                                        onClick={() => removeExtraItem(i)}
                                        className="h-6 w-6 rounded-none text-rose-600 hover:bg-rose-50"
                                        data-testid={`sp-extra-remove-${i}`}
                                        title="İlave kalemi sil">
                                  <X size={12} weight="bold" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-[#1B4776] bg-slate-50">
                          <td colSpan={8} className="px-2 py-2 text-right font-bold text-[10px] uppercase tracking-widest text-slate-600">
                            Brüt İş Bedeli (KDV Hariç)
                          </td>
                          <td className="px-2 py-2 text-right font-mono font-bold text-[#1B4776]">
                            ₺{fmt(calc?.gross)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {hasExtras && (
                    <label className="mt-2 flex items-center gap-2 p-2.5 border border-amber-300 bg-amber-50/70 cursor-pointer text-xs text-amber-900 hover:bg-amber-100/70 transition-colors"
                           data-testid="sp-push-extras-wrap">
                      <input type="checkbox"
                             checked={pushExtrasToContract}
                             onChange={(e) => setPushExtrasToContract(e.target.checked)}
                             className="w-4 h-4 accent-amber-600"
                             data-testid="sp-push-extras-cb" />
                      <span>
                        <strong className="font-bold">Bu ilaveleri sözleşmeye de ekle</strong>
                        <span className="text-[10px] block text-amber-800/80">
                          İşaretlerseniz ilave kalemler sonraki hakedişlerde otomatik görünür ve sözleşme toplamı güncellenir. İşaretlemezseniz sadece bu hakedişte kalır (fark rapora yansır).
                        </span>
                      </span>
                    </label>
                  )}
                </div>

                {/* Kesinti Oranları */}
                <div className="p-3 bg-amber-50/50 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 mb-2 flex items-center gap-2">
                    <Calculator size={12} weight="bold" />
                    Kesinti Oranları (Otomatik — Bu Hakediş İçin Değiştirebilirsiniz)
                    {(form.formality || "resmi") === "gayri_resmi" && (
                      <span className="ml-2 text-[9px] text-amber-800 font-normal">
                        (Gayri Resmi: Tevkifat / Damga / Stopaj uygulanmaz)
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">KDV Tevkifatı</label>
                      <Select value={form.kdv_tevkifat}
                              onValueChange={(v) => setForm((f) => ({ ...f, kdv_tevkifat: v }))}
                              disabled={(form.formality || "resmi") === "gayri_resmi"}>
                        <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sp-tevkifat-trigger">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TEVKIFAT_OPTIONS.map((o) => (
                            <SelectItem key={o} value={o}>{o === "yok" ? "Yok" : o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Damga %</label>
                      <Input type="number" step="0.001" value={form.damga_rate}
                             onChange={(e) => setForm((f) => ({ ...f, damga_rate: parseFloat(e.target.value) || 0 }))}
                             disabled={(form.formality || "resmi") === "gayri_resmi"}
                             data-testid="sp-damga-rate"
                             className="rounded-none sm:rounded-sm font-mono disabled:bg-slate-100 disabled:text-slate-400" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500" title="Sözleşme genel Kesin Teminat oranı — bu hakedişte 0 girerseniz bu ay kesinti yapılmaz, önceki hakedişlerde kesilenler bakiyede kalır">
                        Kesin Teminat %
                      </label>
                      <Input type="number" step="0.1" value={form.retention_rate}
                             onChange={(e) => setForm((f) => ({ ...f, retention_rate: parseFloat(e.target.value) || 0 }))}
                             data-testid="sp-teminat-rate"
                             className="rounded-none sm:rounded-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500" title="Geçici Teminat — 0 girerseniz bu ay geçici teminat kesilmez">
                        Geçici Teminat %
                      </label>
                      <Input type="number" step="0.1"
                             value={form.provisional_retention_rate ?? 0}
                             onChange={(e) => setForm((f) => ({ ...f, provisional_retention_rate: parseFloat(e.target.value) || 0 }))}
                             data-testid="sp-gecici-teminat-rate"
                             className="rounded-none sm:rounded-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Stopaj %</label>
                      <Input type="number" step="0.1" value={form.stopaj_rate}
                             onChange={(e) => setForm((f) => ({ ...f, stopaj_rate: parseFloat(e.target.value) || 0 }))}
                             disabled={(form.formality || "resmi") === "gayri_resmi"}
                             data-testid="sp-stopaj-rate"
                             className="rounded-none sm:rounded-sm font-mono disabled:bg-slate-100 disabled:text-slate-400" />
                    </div>
                  </div>
                </div>

                {/* Avans Mahsubu */}
                <div className="p-3 bg-blue-50/50 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776]">
                      Avans Mahsubu
                    </div>
                    <div className="text-xs text-slate-600">
                      Kalan Avans: <span className="font-mono font-bold text-[#1B4776]">₺{fmt(form.advance_remaining)}</span>
                    </div>
                  </div>
                  <Input type="number" step="0.01" value={form.advance_deduction}
                         onChange={(e) => setForm((f) => ({ ...f, advance_deduction: parseFloat(e.target.value) || 0 }))}
                         placeholder="Bu hakedişte mahsup edilecek avans tutarı"
                         className="rounded-none sm:rounded-sm font-mono" data-testid="sp-advance" />
                </div>

                {/* Manuel Kesintiler */}
                <div>
                  <div className="flex items-center justify-between mb-2 pb-1 border-b border-slate-200">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
                      Ek Kesintiler (Manuel — İsteğe Bağlı)
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={addManualDed}
                            className="h-7 text-xs rounded-none sm:rounded-sm" data-testid="sp-add-manual">
                      <Plus size={12} weight="bold" className="mr-1" /> Kesinti Ekle
                    </Button>
                  </div>
                  {(form.manual_deductions || []).length === 0 && (
                    <div className="text-[10px] text-slate-400 italic">Ek kesinti yok</div>
                  )}
                  {(form.manual_deductions || []).map((d, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`sp-manual-${i}`}>
                      <Input value={d.name} onChange={(e) => setManualDed(i, "name", e.target.value)}
                             placeholder="Kesinti adı (Malzeme, SGK, Cezai...)"
                             className="col-span-4 h-8 rounded-none sm:rounded-sm text-xs" />
                      <Input type="number" value={d.base} onChange={(e) => setManualDed(i, "base", parseFloat(e.target.value) || 0)}
                             placeholder="Baz Tutar" className="col-span-2 h-8 rounded-none sm:rounded-sm text-xs font-mono text-right" />
                      <Input type="number" step="0.01" value={d.rate} onChange={(e) => setManualDed(i, "rate", parseFloat(e.target.value) || 0)}
                             placeholder="Oran %" className="col-span-2 h-8 rounded-none sm:rounded-sm text-xs font-mono text-right" />
                      <Input type="number" step="0.01" value={d.amount} onChange={(e) => setManualDed(i, "amount", parseFloat(e.target.value) || 0)}
                             placeholder="Tutar ₺" className="col-span-3 h-8 rounded-none sm:rounded-sm text-xs font-mono text-right" />
                      <button type="button" onClick={() => rmManualDed(i)}
                              className="col-span-1 text-slate-400 hover:text-rose-600 flex items-center justify-center">
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Özet Panel */}
                <div className="bg-slate-50 border-2 border-[#1B4776] p-4" data-testid="sp-summary">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#1B4776] mb-3 flex items-center gap-2">
                    <span>Hakediş Özeti</span>
                    {(form.formality || "resmi") === "gayri_resmi" && (
                      <span className="px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 text-[9px] font-bold tracking-widest rounded-sm"
                            data-testid="sp-summary-gy-badge">
                        GAYRİ RESMİ · KDV / VERGİSEL KESİNTİ YOK
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Brüt İş Bedeli:</span>
                      <span className="font-mono font-bold" data-testid="sp-summary-gross">₺{fmt(calc?.gross)}</span>
                    </div>
                    {(form.formality || "resmi") !== "gayri_resmi" ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-600">KDV ({form.vat_rate ?? 0}%):</span>
                          <span className="font-mono" data-testid="sp-summary-vat">₺{fmt(calc?.vat)}</span>
                        </div>
                        <div className="col-span-2 flex justify-between border-t border-slate-300 pt-1 mt-1">
                          <span className="text-slate-800 font-semibold">Fatura Toplamı (KDV Dahil):</span>
                          <span className="font-mono font-bold text-slate-800" data-testid="sp-summary-total">₺{fmt(calc?.grossWithVat)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="col-span-2 flex justify-between border-t border-slate-300 pt-1 mt-1">
                        <span className="text-slate-800 font-semibold">Toplam Tutar:</span>
                        <span className="font-mono font-bold text-slate-800" data-testid="sp-summary-total">₺{fmt(calc?.gross)}</span>
                      </div>
                    )}
                    <div className="col-span-2 pt-2 mt-1 border-t border-slate-300">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">Kesintiler:</div>
                      {calc?.autoDed.map((d, i) => (
                        <div key={i} className="flex justify-between text-xs text-slate-600 py-0.5">
                          <span>• {d.name} {d.rate > 0 && `(${d.rate.toFixed(2)}%)`}</span>
                          <span className="font-mono">−₺{fmt(d.amount)}</span>
                        </div>
                      ))}
                      {calc?.manualDed.map((d, i) => (
                        <div key={"m" + i} className="flex justify-between text-xs text-slate-600 py-0.5">
                          <span>• {d.name || "Manuel"}</span>
                          <span className="font-mono">−₺{fmt(d.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="col-span-2 flex justify-between border-t border-slate-300 pt-1">
                      <span className="text-amber-700 font-semibold">Toplam Kesinti:</span>
                      <span className="font-mono font-bold text-amber-700">−₺{fmt(calc?.totalDed)}</span>
                    </div>
                    <div className="col-span-2 flex justify-between border-t-2 border-[#556B10] pt-2 mt-1">
                      <span className="text-lg font-bold text-[#556B10]">NET ÖDENECEK:</span>
                      <span className="text-lg font-mono font-bold text-[#556B10]" data-testid="sp-net-payable">
                        ₺{fmt(calc?.netPayable)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Durum & Notlar */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</label>
                    <Select value={form.status}
                            onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sp-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="taslak">Taslak</SelectItem>
                        <SelectItem value="onaylandi">Onaylandı</SelectItem>
                        <SelectItem value="odendi">Ödendi</SelectItem>
                        <SelectItem value="iptal">İptal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar</label>
                  <Textarea rows={2} value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            className="rounded-none sm:rounded-sm" />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none sm:rounded-sm">İptal</Button>
                <Button onClick={save}
                        className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                        data-testid="sp-save">
                  {editing ? "Güncelle" : "Hakediş Oluştur"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
